import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Home, Lock } from 'lucide-react';
import { ItemVisual } from '../components/ItemVisual';
import { Piko } from '../components/Piko';
import { audio } from '../core/audio';
import { getState, unlockGardenItem } from '../core/storage';
import { lang } from '../core/i18n';
import { GARDEN_ITEMS, GARDEN_SLOTS } from '../content/collection';
import { useEntitlement } from '../core/entitlement';

function ItemArt({ item, size = 56 }) {
  if (item.img) return <img src={item.img} alt="" className="pointer-events-none rounded-2xl" style={{ width: size, height: size, objectFit: 'cover' }} draggable={false} />;
  if (item.emoji) return <span style={{ fontSize: size * 0.8 }} className="pointer-events-none">{item.emoji}</span>;
  return <ItemVisual item={{ kind: item.kind, color: item.color, id: item.id }} size={size} />;
}

export default function PikoGarden() {
  const nav = useNavigate();
  const { entitlement } = useEntitlement();
  const [st, setSt] = useState(getState());
  const en = lang() === 'en';
  const isPro = entitlement?.plan === 'PRO';

  useEffect(() => { audio.init(); audio.music('theme_calm'); audio.playVoice('garden_intro'); return () => audio.stopVoice(); }, []);

  const buy = (item) => {
    if (!item.free && !isPro) { audio.playVoice('hint'); return; }
    if (unlockGardenItem(item.id, item.cost)) {
      audio.playSfx('reward');
      setSt(getState());
    } else audio.playSfx('retry');
  };

  return (
    <div className="fixed inset-0 overflow-hidden">
      <div className="absolute inset-0 hidden md:block" style={{ backgroundImage: 'url(/assets/backgrounds/bg_garden.webp)', backgroundSize: 'cover', filter: 'blur(26px) brightness(0.85)', transform: 'scale(1.15)' }} />
      <div className="relative mx-auto h-[100dvh] w-full md:max-w-[520px] flex flex-col md:shadow-2xl" style={{ background: '#FCF9F2' }} data-testid="piko-garden">
        <div className="relative flex-1 overflow-hidden" style={{ backgroundImage: 'url(/assets/backgrounds/bg_garden.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="flex items-center justify-between px-4 pt-4">
            <button data-testid="garden-home-btn" onClick={() => { audio.stopMusic(); nav('/home'); }}
              className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow">
              <Home size={26} strokeWidth={3} className="text-navy/70" />
            </button>
            <div className="flex items-center gap-2 bg-white/85 backdrop-blur rounded-full px-4 py-2 shadow" data-testid="garden-star-count">
              <Star className="text-sunshine fill-sunshine" size={24} />
              <span className="font-heading font-bold text-lg text-navy">{st.progress.stars}</span>
            </div>
          </div>
          <div className="absolute bottom-4 left-4"><Piko pose="happy" size={92} /></div>
          {st.collection.placed.map((id, i) => {
            const item = GARDEN_ITEMS.find((x) => x.id === id);
            const slot = GARDEN_SLOTS[i % GARDEN_SLOTS.length];
            if (!item) return null;
            return (
              <motion.div key={id} initial={{ scale: 0, y: -18 }} animate={{ scale: 1, y: [0, -4, 0] }}
                transition={{ scale: { type: 'spring' }, y: { repeat: Infinity, duration: 3 + (i % 3) } }}
                className="absolute" style={{ left: `${slot.x}%`, top: `${slot.y}%`, transform: 'translate(-50%,-50%)' }}
                data-testid={`garden-item-${id}`}>
                <ItemArt item={item} size={52} />
              </motion.div>
            );
          })}
        </div>
        <div className="bg-white/95 backdrop-blur rounded-t-[2rem] px-4 pt-4 pb-6 shadow-2xl" style={{ maxHeight: '38%', overflowY: 'auto' }}>
          <p className="font-heading font-bold text-navy mb-3">{en ? 'Use stars to decorate' : 'Guna bintang untuk hiasan'}</p>
          <div className="grid grid-cols-4 gap-3">
            {GARDEN_ITEMS.map((item) => {
              const owned = st.collection.items.includes(item.id);
              const gated = !item.free && !isPro;
              const afford = st.progress.stars >= item.cost;
              return (
                <button key={item.id} data-testid={`shop-item-${item.id}`} disabled={owned}
                  onClick={() => buy(item)}
                  className={`relative flex flex-col items-center gap-1 rounded-2xl p-2 min-h-[86px] ${owned ? 'bg-leaf/20' : 'bg-cream'}`}
                  style={{ opacity: owned ? 0.65 : gated || !afford ? 0.6 : 1 }}>
                  <ItemArt item={item} size={42} />
                  {owned ? (
                    <span className="font-body text-[10px] font-bold text-green-700">✓</span>
                  ) : gated ? (
                    <span className="flex items-center gap-0.5 font-body text-[10px] font-bold text-navy/60"><Lock size={9} /> PRO</span>
                  ) : (
                    <span className="flex items-center gap-0.5 font-body text-[11px] font-bold text-navy"><Star size={11} className="text-sunshine fill-sunshine" />{item.cost}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
